import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Refresh,
  Save,
  Cancel,
  MeetingRoom
} from '@mui/icons-material';

const TYPES_SALLE = ['Salle de cours', 'Amphithéâtre', 'Salle TD', 'Salle TP', 'Laboratoire', 'Salle informatique', 'Réunion'];

const Salles = () => {
  const [salles, setSalles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreActif, setFiltreActif] = useState('toutes');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    short: '',
    capacite: 30,
    type: '',
    batiment: '',
    actif: true
  });

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  });

  const loadAll = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/salles', { headers: getAuthHeaders() });
      setSalles(res.data || []);
      setError('');
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Cette action est réservée aux administrateurs.');
      } else {
        setError('Impossible de charger les salles');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filtered = salles.filter((s) => {
    const t = searchTerm.toLowerCase();
    const texte =
      String(s.name || '').toLowerCase().includes(t) ||
      String(s.short || '').toLowerCase().includes(t) ||
      String(s.batiment || '').toLowerCase().includes(t) ||
      String(s.type || '').toLowerCase().includes(t);
    if (!texte) return false;
    if (filtreActif === 'actives') return s.actif !== false;
    if (filtreActif === 'inactives') return s.actif === false;
    return true;
  });

  const totalPlaces = filtered.reduce((total, s) => total + (parseInt(s.capacite, 10) || 0), 0);
  const sansCapacite = filtered.filter((s) => !parseInt(s.capacite, 10)).length;

  const handleOpenAdd = () => {
    setModalMode('add');
    setSelectedItem(null);
    setFormData({ name: '', short: '', capacite: 30, type: '', batiment: '', actif: true });
    setModalOpen(true);
  };

  const handleOpenEdit = (salle) => {
    setModalMode('edit');
    setSelectedItem(salle);
    setFormData({
      name: salle.name || '',
      short: salle.short || '',
      capacite: salle.capacite || 30,
      type: salle.type || '',
      batiment: salle.batiment || '',
      actif: salle.actif !== false
    });
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validate = () => {
    const errs = {};
    if (!String(formData.name).trim()) errs.name = 'Le nom est obligatoire';
    const c = parseInt(formData.capacite, 10);
    if (!Number.isFinite(c) || c <= 0) errs.capacite = 'La capacité doit être un entier positif';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setError(Object.values(errs)[0]);
      return;
    }
    try {
      setSubmitting(true);
      const headers = getAuthHeaders();
      const payload = {
        name: formData.name,
        short: formData.short,
        capacite: parseInt(formData.capacite, 10),
        type: formData.type,
        batiment: formData.batiment,
        actif: formData.actif
      };
      if (modalMode === 'add') {
        await axios.post('/api/salles', payload, { headers });
        setSuccessMessage('Salle créée');
      } else {
        await axios.put(`/api/salles/${selectedItem._id}`, payload, { headers });
        setSuccessMessage('Salle modifiée');
      }
      handleClose();
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l’enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (salle) => {
    const cible = String(salle.name);
    const avertissement =
      'Une salle utilisée par une séance programmée ne peut pas être supprimée : le système vous le dira.\n\n';
    if (!window.confirm(`${avertissement}Supprimer la salle « ${cible} » ?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`/api/salles/${salle._id}`, { headers });
      setSuccessMessage(`Salle « ${cible} » supprimée`);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Gestion des salles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Les salles saisies ici sont celles proposées dans l’emploi du temps. Leur identifiant est
            attribué automatiquement et n’est pas modifiable : les séances déjà programmées y font
            référence.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Recharger">
            <IconButton onClick={loadAll}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
            Nouvelle salle
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Rechercher une salle, un bâtiment, un type..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
            )
          }}
          sx={{ minWidth: 320 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>État</InputLabel>
          <Select
            label="État"
            value={filtreActif}
            onChange={(e) => { setFiltreActif(e.target.value); setPage(0); }}
          >
            <MenuItem value="toutes">Toutes</MenuItem>
            <MenuItem value="actives">Actives</MenuItem>
            <MenuItem value="inactives">Inactives</MenuItem>
          </Select>
        </FormControl>
        <Chip label={`${filtered.length} salle(s)`} />
        <Chip label={`${totalPlaces} places`} color="info" variant="outlined" />
        {sansCapacite > 0 && (
          <Chip label={`${sansCapacite} sans capacité renseignée`} color="warning" />
        )}
      </Stack>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Salle</TableCell>
                <TableCell>Sigle</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Bâtiment</TableCell>
                <TableCell align="right">Capacité</TableCell>
                <TableCell>Identifiant</TableCell>
                <TableCell>État</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((salle) => (
                  <TableRow key={salle._id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <MeetingRoom fontSize="small" color="action" />
                        {salle.name}
                      </Stack>
                    </TableCell>
                    <TableCell>{salle.short || '—'}</TableCell>
                    <TableCell>{salle.type || '—'}</TableCell>
                    <TableCell>{salle.batiment || '—'}</TableCell>
                    <TableCell align="right">
                      {parseInt(salle.capacite, 10) ? salle.capacite : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={salle.id || '—'} />
                    </TableCell>
                    <TableCell>
                      {salle.actif !== false
                        ? <Chip size="small" color="success" label="Active" />
                        : <Chip size="small" label="Inactive" />}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => handleOpenEdit(salle)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" color="error" onClick={() => handleDelete(salle)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucune salle
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          rowsPerPageOptions={[10, 15, 25, 50]}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalMode === 'add' ? 'Nouvelle salle' : `Modifier « ${selectedItem?.name} »`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              name="name" label="Nom de la salle" value={formData.name} onChange={handleChange}
              placeholder="Salle A03"
            />
            <TextField
              name="short" label="Sigle" value={formData.short} onChange={handleChange}
              helperText="Laissez vide pour reprendre le nom"
            />
            <Divider>Occupation</Divider>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select name="type" label="Type" value={formData.type} onChange={handleChange} displayEmpty>
                <MenuItem value="">
                  <em>Non précisé</em>
                </MenuItem>
                {TYPES_SALLE.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              name="capacite" label="Capacité (places)" type="number" value={formData.capacite}
              onChange={handleChange} inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              name="batiment" label="Bâtiment" value={formData.batiment} onChange={handleChange}
              placeholder="Bâtiment B"
            />
            <FormControl fullWidth size="small">
              <InputLabel>État</InputLabel>
              <Select
                name="actif" label="État" value={formData.actif ? 'oui' : 'non'}
                onChange={(e) => setFormData({ ...formData, actif: e.target.value === 'oui' })}
              >
                <MenuItem value="oui">Active</MenuItem>
                <MenuItem value="non">Inactive</MenuItem>
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Une salle inactive reste affectée aux séances déjà programmées, mais n’est plus
                proposée à la sélection.
              </Typography>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} startIcon={<Cancel />}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" startIcon={<Save />} disabled={submitting}>
            {submitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

export default Salles;